import { Component } from '@theme/component';

/**
 * <zg-contact-form>: the role-dependent territory checkboxes of sections/zg-contact-form.liquid.
 *
 * The section renders the role drop-down (ref="role") with the option that shows the territory checkboxes marked
 * `data-zg-territory-role`, and the checkboxes in a fieldset (ref="territory"), `hidden` unless that option is
 * the first; a <noscript> style shows them when scripts don't run. This element
 * - shows the fieldset while that role is selected and hides and disables it otherwise, so ticks made for it
 *   aren't sent with another role (and come back if the shopper returns to it). A change the shopper makes is
 *   said in the polite status (ref="status"); the state on load, including a role the browser restored or one
 *   picked before this ran, is applied silently;
 * - on load and on hashchange, when the URL fragment equals `data-role-hash` (ignoring case), selects that role
 *   and scrolls the section into view, so its heading and contact line show above the form: at once on load or
 *   with reduced motion, smoothly otherwise. scrollIntoView scrolls whichever element scrolls the page
 *   (.page-wrapper from 990px, where window.scrollTo does nothing); the section's scroll margin keeps it clear of
 *   the sticky header.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLSelectElement} [role] - Absent when the section has no role options.
 * @property {HTMLFieldSetElement} [territory] - Absent without territory options or a matching role.
 * @property {HTMLElement} [status] - Rendered with the territory fieldset.
 */

/** @extends {Component<Refs>} */
class ZgContactForm extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    if (!this.#applyHash('instant')) this.#update(false);
    window.addEventListener('hashchange', this.#onHashChange, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  /** on:change of the role drop-down. */
  selectRole() {
    this.#update(true);
  }

  #onHashChange = () => {
    this.#applyHash('smooth');
  };

  /**
   * Shows the territory fieldset while its role is selected; hides and disables it otherwise.
   * @param {boolean} announce - Say the change in the status region.
   */
  #update(announce) {
    const { role, territory, status } = this.refs;
    if (!territory) return;

    const show = role?.selectedOptions[0]?.hasAttribute('data-zg-territory-role') ?? false;
    const changed = territory.hidden === show;
    territory.hidden = !show;
    territory.disabled = !show;

    if (announce && changed && status) {
      status.textContent = (show ? status.dataset.shown : status.dataset.hidden) ?? '';
    }
  }

  /**
   * Selects the territory role and scrolls to the section when the URL fragment is the role link.
   * @param {ScrollBehavior} behavior - Used unless the shopper prefers reduced motion.
   * @returns {boolean} Whether the fragment matched.
   */
  #applyHash(behavior) {
    const hash = this.dataset.roleHash;
    const option = this.refs.role?.querySelector('option[data-zg-territory-role]');
    if (!hash || !(option instanceof HTMLOptionElement)) return false;

    let fragment = '';
    try {
      fragment = decodeURIComponent(window.location.hash.slice(1));
    } catch {
      return false;
    }
    if (fragment.toLowerCase() !== hash.toLowerCase()) return false;

    option.selected = true;
    this.#update(false);

    const target = this.closest('.zg-contact-form') ?? this.closest('form') ?? this;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'instant' : behavior });
    return true;
  }
}

if (!customElements.get('zg-contact-form')) {
  customElements.define('zg-contact-form', ZgContactForm);
}
